import { initializeApp, getApps, type FirebaseApp } from 'firebase/app'
import { getToken as getAppCheckToken, initializeAppCheck, ReCaptchaEnterpriseProvider, type AppCheck } from 'firebase/app-check'
import {
  browserSessionPersistence,
  connectAuthEmulator,
  getAuth,
  setPersistence,
  type Auth
} from 'firebase/auth'
import { connectFirestoreEmulator, getFirestore, memoryLocalCache, initializeFirestore, type Firestore } from 'firebase/firestore'
import { connectFunctionsEmulator, getFunctions, type Functions } from 'firebase/functions'

export interface PmmFirebaseServices {
  app: FirebaseApp
  auth: Auth
  appCheck: AppCheck | null
  firestore: Firestore
  functions: Functions
}

let services: PmmFirebaseServices | null = null
let emulatorConnected = false

function required(value: string | undefined, name: string): string {
  if (!value) throw new Error(`Missing PMM Firebase setting: ${name}`)
  return value
}

export async function getPmmFirebaseServices(): Promise<PmmFirebaseServices> {
  if (services) return services

  const appId = required(import.meta.env.VITE_FIREBASE_APP_ID, 'VITE_FIREBASE_APP_ID')
  const messagingSenderId = import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID?.trim() || appId.split(':')[1]
  const config = {
    apiKey: required(import.meta.env.VITE_FIREBASE_API_KEY, 'VITE_FIREBASE_API_KEY'),
    authDomain: required(import.meta.env.VITE_FIREBASE_AUTH_DOMAIN, 'VITE_FIREBASE_AUTH_DOMAIN'),
    projectId: required(import.meta.env.VITE_FIREBASE_PROJECT_ID, 'VITE_FIREBASE_PROJECT_ID'),
    appId,
    messagingSenderId: required(messagingSenderId, 'VITE_FIREBASE_MESSAGING_SENDER_ID')
  }

  const app = getApps()[0] ?? initializeApp(config)

  const appCheckMode = import.meta.env.VITE_PMM_APP_CHECK_MODE ?? 'disabled'
  let appCheck: AppCheck | null = null
  const siteKey = import.meta.env.VITE_PMM_APP_CHECK_SITE_KEY
  if (appCheckMode !== 'disabled' && siteKey) {
    appCheck = initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(siteKey),
      isTokenAutoRefreshEnabled: true
    })
  }

  const auth = getAuth(app)
  await setPersistence(auth, browserSessionPersistence)

  let firestore: Firestore
  try {
    firestore = initializeFirestore(app, { localCache: memoryLocalCache() })
  } catch {
    firestore = getFirestore(app)
  }
  const functions = getFunctions(app, 'australia-southeast1')

  if (import.meta.env.VITE_PMM_DATA_MODE === 'emulator' && !emulatorConnected) {
    connectAuthEmulator(auth, 'http://127.0.0.1:9199', { disableWarnings: true })
    connectFirestoreEmulator(firestore, '127.0.0.1', 8187)
    connectFunctionsEmulator(functions, '127.0.0.1', 5107)
    emulatorConnected = true
  }

  services = { app, appCheck, auth, firestore, functions }
  return services
}

export async function ensurePmmAppCheckReady(forceRefresh = false): Promise<void> {
  const { appCheck } = await getPmmFirebaseServices()
  if (appCheck) await getAppCheckToken(appCheck, forceRefresh)
}
